/**
 * useChatFileIngest — the single ingest path for chat attachments.
 *
 * The file-picker button, clipboard paste (Ctrl/Cmd+V), and drag-and-drop all
 * funnel raw File[] through this hook so they apply identical compression,
 * PDF text-extraction, size/type validation, and max-image rules. Shared by
 * ChatFileUpload (button) and ChatComposer (paste/drop) so both surfaces —
 * Conversations and Quick Study — behave the same with zero duplication.
 */

import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { QUESTIONNAIRE_ASSET_VIDEO_MIME_TYPES } from '@digital-twins/shared-types'

import i18n from '@/i18n'
import { compressImage, compressCaptureForChat } from '@/lib/utils/compress-image'
import { extractPdfText, PdfExtractionResult, renderPdfToStitchedImageBase64 } from '@/lib/utils/extract-pdf-text'
import { ChatImage } from '@/lib/api'

// The injected creative hoster the Quick Study surface passes into this hook (externalize-creatives,
// Step C) so this SHARED ingest hook never hard-wires a Quick-Study endpoint. Returns the hosted ref;
// the caller keeps the in-memory base64 for instant preview + immediate vision. Owned by the
// components/chat layer (defined here) so nothing under components/ imports from pages/ — the QS
// uploader in pages/ imports THIS type (pages→components is allowed). Resolving `null` means
// "this surface does not host this media type": the tile passes through unchanged, no failure.
export type UploadHosted = (img: {
  base64: string
  mimeType: string
  /** Original `File.name` for storage; user-facing `ChatImage.label` stays unsanitized. */
  filename?: string
}) => Promise<{ url: string; objectKey?: string; fileId?: string } | null>

// Bound each hosting upload: a hung upload must never hold the single post-upload emit open, so an
// upload that exceeds this deadline is treated exactly like a rejected one (best-effort base64-only
// fallback; the on-open backfill / a later save re-hosts it). 15s is generous for one compressed image.
// Exported so tests can pin the exact deadlines instead of magic numbers.
export const HOST_UPLOAD_TIMEOUT_MS = 15_000
// A video creative uploads its raw bytes (tens of MB, no compression), so it gets a far longer
// deadline than a compressed image before falling back to base64-only.
export const HOST_VIDEO_UPLOAD_TIMEOUT_MS = 120_000

// The ONLY video containers the backend analyzer accepts. Shared single source of truth: the
// ingest gate, the <input accept> attributes, and the upload-panel copy must all agree, or a
// picked file sails through the picker and dies server-side.
// The upload policy's video types (shared-types), the ones the upload route takes.
export const QS_VIDEO_MIME_TYPES = QUESTIONNAIRE_ASSET_VIDEO_MIME_TYPES

// Membership check on the NORMALIZED type (lowercase, `;codecs=` parameters stripped) — a
// `video/webm;codecs=vp9` file is the same container as `video/webm`. Never a bare
// startsWith('video/') gate: that would wave through containers the analyzer rejects (avi, mkv).
export function isSupportedVideoMime(type: string | null | undefined): boolean {
  const normalized = (type ?? '').toLowerCase().split(';')[0].trim()
  return (QS_VIDEO_MIME_TYPES as readonly string[]).includes(normalized)
}

// Reject `promise` if it hasn't settled within `ms`, so the batch emit always resolves in bounded
// time even if the injected uploader hangs. The timer is cleared as soon as the wrapped promise
// settles; a late settlement after a timeout is a harmless no-op (handlers already attached, so no
// unhandled rejection). Pure + module-scoped for unit coverage.
export function withUploadTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`upload timed out after ${ms}ms`)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

// How long to wait for a detached <video> to report its metadata before letting the file through.
export const VIDEO_METADATA_TIMEOUT_MS = 4000

/** Duration in seconds from the file's own metadata, or null when it cannot be read in time. */
export function readVideoDurationSeconds(file: File, timeoutMs: number = VIDEO_METADATA_TIMEOUT_MS): Promise<number | null> {
  return new Promise((resolve) => {
    let objectUrl: string | null = null
    let video: HTMLVideoElement | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    const finish = (value: number | null) => {
      if (timer) {
        clearTimeout(timer)
      }
      if (video) {
        video.removeAttribute('src')
        video.onloadedmetadata = null
        video.onerror = null
      }
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl)
        } catch {
          // best effort
        }
      }
      resolve(value)
    }
    try {
      objectUrl = URL.createObjectURL(file)
      video = document.createElement('video')
      video.preload = 'metadata'
      video.onloadedmetadata = () => {
        const seconds = video?.duration
        finish(typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : null)
      }
      video.onerror = () => finish(null)
      timer = setTimeout(() => finish(null), timeoutMs)
      video.src = objectUrl
    } catch {
      finish(null)
    }
  })
}

interface UseChatFileIngestOptions {
  images: Array<ChatImage>
  onImagesChange: (images: Array<ChatImage>) => void
  /**
   * MERGE-SAFE emits (video hosting). A video's hosting upload holds the batch open for up
   * to HOST_VIDEO_UPLOAD_TIMEOUT_MS (120s); in that window the user can send (clearing the
   * list), remove a tile, or ingest a second batch, so a whole-array `onImagesChange`
   * overwrite from the state captured at ingest start would resurrect cleared tiles or
   * clobber the concurrent batch. When this is provided (QS passes its React state setter),
   * every emit is a functional merge keyed by creative identity against the CURRENT list:
   * additions append, hosted refs patch in place (preserving notes/labels typed meanwhile),
   * removals stay removed, and a video whose hosting FAILED is dropped outright (its base64
   * must never ride a sent message into persistence; the toast already says to re-add it).
   * Omit it and the emits are byte-for-byte the legacy plain-array behavior.
   */
  onImagesMerge?: (updater: (prev: Array<ChatImage>) => Array<ChatImage>) => void
  onPdfChange: (text: string | null, name: string | null) => void
  maxImages?: number
  maxSizeMB?: number
  /** Per-VIDEO size cap (MB). Videos are hosted raw (no client compression), so they get a
   *  larger ceiling than images; 300MB is the inline-base64 transport ceiling
   *  (V8 max string ~512MB); multipart surfaces allow 500MB. */
  maxVideoSizeMB?: number
  /** Opt-in per-VIDEO duration cap (seconds). Only the room composers pass it (300); every other
   *  surface omits it and is unchanged. Read from the file's own metadata before upload; when the
   *  metadata cannot be read the file passes (the server timeouts still bound it). */
  maxVideoSeconds?: number
  /**
   * Quick Study creative hosting (externalize-creatives, Step C). When provided, each
   * freshly-compressed image is uploaded via this callback and the returned `{url,objectKey}`
   * is attached to the emitted `ChatImage` (the base64 is kept in memory for instant preview +
   * immediate vision — only the hosted ref is persisted downstream). SHARED-HOOK SAFE: omit it
   * and behavior is byte-for-byte unchanged — Conversations never sets it, so its chat images
   * are never sent to the Quick Study endpoint. Best-effort: an upload failure keeps the
   * base64-only image and never blocks or fails the ingest. Uploads for a multi-image batch run
   * in parallel, so one slow upload never serializes the rest, and each upload is TIME-BOUNDED
   * (HOST_UPLOAD_TIMEOUT_MS) so a hung upload can never stall the emit indefinitely.
   */
  uploadHosted?: UploadHosted
  /**
   * Treat PDFs as VISUAL stimuli (default): render each page to an image the
   * twins can SEE — the same path as attached images — rather than extracting
   * text. This is on everywhere so PDFs behave exactly like images across chat
   * surfaces (landing pages / ads are visual and are often image-based PDFs with
   * no text layer). Set to `false` only for a surface that genuinely needs the
   * PDF's text instead of its rendered pages.
   */
  pdfAsImages?: boolean
  /**
   * Keep the original PDF on its rendered tile as `sourceDocument` (Step 10c), so a
   * room send can persist the file as a room material. The twins still see the
   * rendered image only. Off by default: other surfaces never carry the bytes.
   */
  keepPdfSource?: boolean
  /**
   * @deprecated No longer splits a PDF into per-page images. A PDF is now always
   * rendered to ONE full-length image (every page stitched vertically), because a
   * multi-page PDF is one long creative — a landing page exported to letter sheets,
   * an ad spread — and the twins must see the WHOLE page, not just the first sheet.
   * "One creative per file" is therefore the built-in behavior; this prop is
   * retained only so existing call sites keep compiling and is otherwise ignored.
   */
  pdfMaxPagesPerFile?: number
  /**
   * Accept VIDEO creatives (read inline as base64). Off by default — only the
   * Quick Study surface analyzes videos; other chats would send a video as an
   * image and break, so they must NOT ingest video.
   */
  acceptVideo?: boolean
}

export interface UseChatFileIngestResult {
  /** Compress / render the given files and append them to the attachment list. */
  ingestFiles: (files: Array<File>) => Promise<void>
  /**
   * How many image tiles are mid-flight (being compressed / rendered) right now.
   * Surfaces render this many loading skeletons so an upload registers instantly
   * instead of looking like it failed during the ~0.5–2s compression delay.
   */
  pendingCount: number
  /**
   * UI-language PDF chip label from the last text extract. Tiles/toasts use this.
   * `onPdfChange` always receives `formatPdfWireLabel` (English) for the model prompt.
   */
  pdfDisplayName: string | null
  /** Drop a stale chip label when the parent clears the attached PDF. */
  clearPdfDisplayName: () => void
}

/**
 * Stable English PDF name for the model / thread body. Independent of uiLanguage.
 */
export function formatPdfWireLabel(fileName: string, pageCount: number, truncated: boolean): string {
  const pageWord = pageCount === 1 ? 'page' : 'pages'
  if (truncated) {
    return `${fileName} (${pageCount} ${pageWord}, truncated)`
  }
  return `${fileName} (${pageCount} ${pageWord})`
}

function formatPdfDisplayLabel(fileName: string, pageCount: number, truncated: boolean): string {
  return i18n.t(
    truncated ? 'chatChrome.ingest.pdfPagesTruncatedLabel' : 'chatChrome.ingest.pdfPagesLabel',
    { ns: 'app', name: fileName, count: pageCount },
  )
}

function withFileLabel(img: ChatImage, file: File): ChatImage {
  return { ...img, label: file.name }
}

/** Read any File as raw base64 (no data: prefix). */
async function readFileBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
  const comma = dataUrl.indexOf(',')
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
}

/**
 * A GIF is sent as-is, the way studies send it: a canvas re-encode would keep only its
 * first frame as a JPEG. Every other raster image is compressed as before.
 */
async function readStillOrAnimatedImage(file: File): Promise<ChatImage> {
  if (isGifFile(file)) {
    return { base64: await readFileBase64(file), mimeType: 'image/gif' }
  }
  return compressImage(file)
}

function isGifFile(file: File): boolean {
  return (file.type ?? '').toLowerCase().split(';')[0].trim() === 'image/gif'
}

/** Read a video File inline as a ChatImage (raw base64 + video/* mimeType), no
 * compression. The base64 is preview-only (tile/lightbox); the RUN binds the hosted
 * url exclusively — attachImages never inlines video bytes into the spec. */
async function readVideoAsChatImage(file: File): Promise<ChatImage> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
  const comma = dataUrl.indexOf(',')
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
  return withFileLabel({ base64, mimeType: file.type || 'video/mp4' } as ChatImage, file)
}

// A real 5:00 cut probes at 300.03 s (container rounding), so a file is too long only when it
// exceeds the surface cap by more than this slack.
const VIDEO_DURATION_TOLERANCE_SECONDS = 1

export function useChatFileIngest({
  images,
  onImagesChange,
  onImagesMerge,
  onPdfChange,
  maxImages = 3,
  maxSizeMB = 10,
  maxVideoSizeMB = 300,
  maxVideoSeconds,
  pdfAsImages = true,
  keepPdfSource = false,
  uploadHosted,
  acceptVideo = false,
}: UseChatFileIngestOptions): UseChatFileIngestResult {
  // Counts image tiles currently being processed so the UI can show skeletons.
  // Increment/decrement (not set/reset) so overlapping batches stay accurate.
  const [pendingCount, setPendingCount] = useState(0)
  const [pdfDisplayName, setPdfDisplayName] = useState<string | null>(null)

  const ingestFiles = useCallback(
    async (files: Array<File>) => {
      if (files.length === 0) return

      const maxBytes = maxSizeMB * 1024 * 1024

      // Best-effort creative hosting: upload a freshly-compressed image and return it with
      // the hosted `{url,objectKey}` attached; on any failure (including a TIMEOUT) keep the
      // base64-only image (never throws, so the ingest can never fail on an upload). The upload
      // is time-bounded so a hung request can't stall the batch emit indefinitely. No-op when no
      // uploader is wired (Conversations) — the image passes through unchanged.
      const hostImage = async (img: ChatImage, timeoutMs: number = HOST_UPLOAD_TIMEOUT_MS): Promise<ChatImage> => {
        if (!uploadHosted) return img
        try {
          const hosted = await withUploadTimeout(
            uploadHosted({
              base64: img.base64,
              mimeType: img.mimeType,
              ...(img.label ? { filename: img.label } : {}),
            }),
            timeoutMs,
          )
          // Not hosted on this surface (e.g. conversation images ride inline): not a failure.
          if (!hosted) return img
          return {
            ...img,
            url: hosted.url,
            ...(hosted.objectKey ? { objectKey: hosted.objectKey } : {}),
            ...(hosted.fileId ? { fileId: hosted.fileId } : {}),
          }
        } catch (e) {
          console.warn(`[chat] creative hosting upload failed; keeping tile with hostFailed: ${String(e)}`)
          // Thread send gates treat missing url as in-flight. Mark the failure terminal so
          // "Uploading…" cannot wedge the composer. Keep the tile so the user can dismiss it.
          const isVideo = Boolean(img.mimeType?.toLowerCase().startsWith('video/'))
          toast.error(
            isVideo
              ? i18n.t('chatChrome.ingest.videoUploadFailed', { ns: 'app' })
              : i18n.t('chatChrome.ingest.imageUploadFailed', { ns: 'app' }),
          )
          return { ...img, hostFailed: true }
        }
      }
      // Uploads are kicked off as each image compresses and awaited together at the end, so a
      // multi-image batch uploads in parallel rather than serializing behind one slow upload.
      // Each job records the index of its image in `nextImages` (legacy emit) plus the tile's
      // pre-hosting base64 as its IDENTITY key (merge emit) so the hosted ref lands back on
      // the right entry even after the current list changed under the batch.
      const hostJobs: Array<{ index: number; key: string; job: Promise<ChatImage> }> = []
      // Every tile this batch appended, in order: the unit the merge emits work from.
      const addedTiles: Array<ChatImage> = []

      // Approximate how many image tiles this batch will add (images, plus PDFs
      // when they render to images), capped by the remaining slots — that's how
      // many skeletons to show while compression runs.
      const remainingSlots = Math.max(0, maxImages - images.length)
      const previewableFiles = files.filter(
        (f) => f.type.startsWith('image/') || (acceptVideo && isSupportedVideoMime(f.type)) || (pdfAsImages && f.type === 'application/pdf'),
      ).length
      const skeletonCount = Math.min(previewableFiles, remainingSlots)
      if (skeletonCount > 0) setPendingCount((c) => c + skeletonCount)
      // Set once the early video emit swaps the skeletons for real tiles, so the finally
      // below cannot double-decrement.
      let skeletonsReleased = false
      try {
        // Accumulate locally: a multi-file batch (paste / drop / multi-select) adds
        // every image, instead of each onImagesChange call clobbering down to the last.
        let nextImages = [...images]
        let addedImage = false
        // Split by media so a video-only batch can emit its tile early (see below) without
        // touching the image path's single-emit contract.
        let addedVideo = false
        let addedNonVideo = false

        for (const file of files) {
          // Type gate FIRST: a wrong-format file must be told it's the format, not tripped up
          // by whichever size ceiling its (rejected) type would have had.
          const isVideoType = (file.type ?? '').toLowerCase().split(';')[0].trim().startsWith('video/')
          if (isVideoType && !acceptVideo) {
            toast.error(i18n.t('chatChrome.ingest.unsupportedFileType', { ns: 'app', name: file.name }))
            continue
          }
          if (isVideoType && !isSupportedVideoMime(file.type)) {
            toast.error(i18n.t('chatChrome.ingest.unsupportedVideoFormat', { ns: 'app' }))
            continue
          }
          const isVideoFile = acceptVideo && isVideoType
          const fileMaxBytes = isVideoFile ? maxVideoSizeMB * 1024 * 1024 : maxBytes
          if (file.size > fileMaxBytes) {
            toast.error(i18n.t('chatChrome.ingest.fileTooLarge', {
              ns: 'app',
              name: file.name,
              limit: isVideoFile ? maxVideoSizeMB : maxSizeMB,
            }))
            continue
          }
          if (isVideoFile && maxVideoSeconds !== undefined) {
            const durationSeconds = await readVideoDurationSeconds(file)
            if (durationSeconds !== null && durationSeconds > maxVideoSeconds + VIDEO_DURATION_TOLERANCE_SECONDS) {
              toast.error(i18n.t('chatChrome.ingest.videoTooLong', {
                ns: 'app',
                name: file.name,
                limit: Math.round(maxVideoSeconds / 60),
              }))
              continue
            }
          }

          if (file.type === 'application/pdf') {
            // Creative-first surfaces render PDF pages to images so the twins SEE
            // them like any attachment — robust for image-based LP/ad PDFs that
            // have no extractable text layer.
            if (pdfAsImages) {
              if (nextImages.length >= maxImages) {
                toast.error(i18n.t('chatChrome.ingest.maxCreatives', { ns: 'app', count: maxImages }))
                break
              }
              // A multi-page PDF is ONE long creative (a landing page exported to
              // letter sheets, an ad spread): stitch every page into a single
              // full-length image so the twins see the WHOLE page — not just the
              // first sheet. One PDF → one image. `compressCaptureForChat` is
              // tall-image aware (caps width, keeps height) so a long strip stays
              // legible and within the vision budget.
              try {
                const stitchedBase64 = await renderPdfToStitchedImageBase64(file)
                if (!stitchedBase64) {
                  toast.error(i18n.t('chatChrome.ingest.pdfReadFailed', { ns: 'app' }))
                  continue
                }
                const rendered = withFileLabel(await compressCaptureForChat(stitchedBase64), file)
                const compressed: ChatImage = keepPdfSource
                  ? {
                      ...rendered,
                      sourceDocument: {
                        base64: await readFileBase64(file),
                        name: file.name,
                        mimeType: 'application/pdf',
                      },
                    }
                  : rendered
                nextImages = [...nextImages, compressed]
                addedTiles.push(compressed)
                addedImage = true
                addedNonVideo = true
                if (uploadHosted) hostJobs.push({ index: nextImages.length - 1, key: compressed.base64, job: hostImage(compressed) })
              } catch {
                toast.error(i18n.t('chatChrome.ingest.pdfProcessFailed', { ns: 'app' }))
              }
              continue
            }

            try {
              const result: PdfExtractionResult = await extractPdfText(file)
              if (!result.text.trim()) {
                toast.error(i18n.t('chatChrome.ingest.pdfTextExtractFailed', { ns: 'app' }))
                continue
              }
              setPdfDisplayName(formatPdfDisplayLabel(file.name, result.pageCount, result.truncated))
              onPdfChange(result.text, formatPdfWireLabel(file.name, result.pageCount, result.truncated))
            } catch {
              toast.error(i18n.t('chatChrome.ingest.pdfProcessFailed', { ns: 'app' }))
            }
            continue
          }

          if (file.type.startsWith('image/')) {
            if (nextImages.length >= maxImages) {
              toast.error(i18n.t('chatChrome.ingest.maxCreatives', { ns: 'app', count: maxImages }))
              break
            }
            try {
              const compressed = withFileLabel(await readStillOrAnimatedImage(file), file)
              nextImages = [...nextImages, compressed]
              addedTiles.push(compressed)
              addedImage = true
              addedNonVideo = true
              if (uploadHosted) hostJobs.push({ index: nextImages.length - 1, key: compressed.base64, job: hostImage(compressed) })
            } catch {
              toast.error(i18n.t('chatChrome.ingest.processFileFailed', { ns: 'app', name: file.name }))
            }
            continue
          }

          if (isVideoFile) {
            // A video creative attaches like an image: read inline as base64 (no
            // compression — it's not an image), carried on the same ChatImage list
            // with a video/* mimeType. It is HOSTED like an image too (with a longer
            // upload deadline for the raw bytes) so the persisted session keeps a
            // `{url,objectKey}` ref and the video survives a reload; the in-memory
            // base64 stays for instant preview / the local-dev fallback.
            if (nextImages.length >= maxImages) {
              toast.error(i18n.t('chatChrome.ingest.maxCreatives', { ns: 'app', count: maxImages }))
              break
            }
            try {
              const video = await readVideoAsChatImage(file)
              nextImages = [...nextImages, video]
              addedTiles.push(video)
              addedImage = true
              addedVideo = true
              if (uploadHosted) hostJobs.push({ index: nextImages.length - 1, key: video.base64, job: hostImage(video, HOST_VIDEO_UPLOAD_TIMEOUT_MS) })
            } catch {
              toast.error(i18n.t('chatChrome.ingest.processFileFailed', { ns: 'app', name: file.name }))
            }
            continue
          }

          toast.error(i18n.t('chatChrome.ingest.unsupportedFileType', { ns: 'app', name: file.name }))
        }

        // Attach the hosted refs (uploads ran in parallel above; `hostImage` never rejects and is
        // TIME-BOUNDED, so this Promise.all — and therefore the emit — always resolves within
        // its deadline even if an upload hangs). Two emit strategies:
        //   - `onImagesMerge` wired (Quick Study): every emit is a FUNCTIONAL MERGE keyed by the
        //     tile's pre-hosting base64 identity, so a send/removal/second-batch during the (up
        //     to 120s) video hosting window can never be clobbered or resurrected by this batch's
        //     late emit. A video whose hosting FAILED is dropped here: its base64 must never
        //     ride a sent message into persistence (localStorage + the 20MB mirror both choke).
        //   - legacy (no merge fn): the original SINGLE plain-array emit, byte-for-byte; those
        //     surfaces never accept video, so the 15s image window keeps the old trade-off.
        if (addedImage) {
          if (hostJobs.length > 0) {
            // VIDEO-ONLY batches get an EARLY first emit with the base64 tiles: a raw video
            // upload can take up to HOST_VIDEO_UPLOAD_TIMEOUT_MS, far past what a skeleton
            // bridges, so the tile must appear now and the merged emit below patches in the
            // hosted refs. Confined to the video-only case ON PURPOSE: only Quick Study (one
            // composer, one active ingest path at a time) accepts video, so an image/mixed
            // batch keeps the strict single emit.
            if (addedVideo && !addedNonVideo) {
              if (onImagesMerge) {
                // Append-only merge: never resets tiles a concurrent actor already changed.
                const additions = [...addedTiles]
                onImagesMerge((prev) => {
                  const have = new Set(prev.map((im) => im.base64).filter(Boolean))
                  const fresh = additions.filter((t) => !have.has(t.base64))
                  return fresh.length > 0 ? [...prev, ...fresh] : prev
                })
              } else {
                onImagesChange([...nextImages])
              }
              // The real tiles are visible now: release the skeletons, or the composer
              // shows tile + skeleton side by side for the whole hosting window.
              if (skeletonCount > 0 && !skeletonsReleased) {
                skeletonsReleased = true
                setPendingCount((c) => Math.max(0, c - skeletonCount))
              }
            }
            const hosted = await Promise.all(hostJobs.map((j) => j.job))
            hostJobs.forEach((j, i) => {
              nextImages[j.index] = hosted[i]
            })
          }
          if (onImagesMerge) {
            // Per-tile batch outcome, keyed by pre-hosting base64: the hosted tile when the
            // upload succeeded, the original otherwise, plus `drop` for a video that failed
            // to host (never persist its bytes; the hostImage toast already said re-add).
            const outcomes = addedTiles.map((tile) => {
              const job = hostJobs.find((j) => j.key === tile.base64)
              const jobIndex = job ? hostJobs.indexOf(job) : -1
              const resolved = jobIndex >= 0 ? nextImages[hostJobs[jobIndex].index] : tile
              const isVideo = (tile.mimeType ?? '').toLowerCase().startsWith('video/')
              return { key: tile.base64, tile: resolved, drop: isVideo && Boolean(uploadHosted) && !resolved.url }
            })
            onImagesMerge((prev) => {
              const next = [...prev]
              for (const o of outcomes) {
                const at = next.findIndex((im) => im.base64 && im.base64 === o.key)
                if (o.drop) {
                  if (at >= 0) next.splice(at, 1)
                  continue
                }
                if (at >= 0) {
                  // Patch the hosted ref onto the CURRENT tile: edits (note/label) made
                  // during the upload window win over the snapshot.
                  next[at] = o.tile.url
                    ? {
                        ...next[at],
                        url: o.tile.url,
                        ...(o.tile.objectKey ? { objectKey: o.tile.objectKey } : {}),
                        ...(o.tile.fileId ? { fileId: o.tile.fileId } : {}),
                      }
                    : o.tile.hostFailed
                      ? { ...next[at], hostFailed: true }
                      : next[at]
                } else if (!addedVideo || addedNonVideo) {
                  // Not emitted early and not removed by the user mid-flight: append, but
                  // never past the cap a concurrent batch may have filled meanwhile.
                  if (next.length < maxImages) {
                    next.push(o.tile)
                  } else {
                    console.warn('[chat] ingest merge: attachment cap reached by a concurrent batch; tile not added')
                  }
                }
                // Early-emitted video tiles absent from `prev` were sent or removed and are
                // deliberately NOT re-added (that resurrection was the bug).
              }
              return next
            })
          } else {
            onImagesChange(nextImages)
          }
        }
      } finally {
        if (skeletonCount > 0 && !skeletonsReleased) setPendingCount((c) => Math.max(0, c - skeletonCount))
      }
    },
    [images, onImagesChange, onImagesMerge, onPdfChange, maxImages, maxSizeMB, maxVideoSizeMB, maxVideoSeconds, pdfAsImages, keepPdfSource, uploadHosted, acceptVideo],
  )

  const clearPdfDisplayName = useCallback(() => {
    setPdfDisplayName(null)
  }, [])

  return { ingestFiles, pendingCount, pdfDisplayName, clearPdfDisplayName }
}
